import React, { useEffect, useState } from "react";
import config from "../../config";
import "./RelayControlSetting.css";

export const DEFAULT_RELAY_SETTINGS = {
  ch1_on: 1,
  ch1_off: 1,
  ch2_on: 50,
  ch2_off: 50,
  ch3_on: 1000,
  ch3_off: 1000,
  ch4_on: 0,
  ch4_off: 0,
};

const CHANNEL_DEFS = [
  {
    key: "ch1",
    title: "CH1 : 인버터 발전 상태",
    unit: "kW",
  },
  {
    key: "ch2",
    title: "CH2 : 풍속",
    unit: "m/s",
  },
  {
    key: "ch3",
    title: "CH3 : 강우량",
    unit: "mm",
  },
  {
    key: "ch4",
    title: "CH4 : 미지정",
    unit: "",
  },
];

function toInputValue(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "";
  }
  return String(value);
}

function ChannelCard({ def, values, onChange, disabled }) {
  const onKey = `${def.key}_on`;
  const offKey = `${def.key}_off`;
  const unitText = def.unit ? ` (${def.unit})` : "";

  return (
    <section className="relay-setting-card">
      <h3 className="relay-setting-card__title">{def.title}</h3>
      <div className="relay-setting-card__rows">
        <div className="relay-setting-row">
          <span className="relay-setting-row__dot relay-setting-row__dot--on" />
          <span className="relay-setting-row__label">차단 기준 값</span>
          <input
            className="relay-setting-row__input"
            type="number"
            step="any"
            value={toInputValue(values[onKey])}
            disabled={disabled}
            onChange={(e) => onChange(onKey, e.target.value)}
            aria-label={`${def.title} 차단 기준 값`}
          />
          <span className="relay-setting-row__text">{unitText}</span>
        </div>

        <div className="relay-setting-row">
          <span className="relay-setting-row__dot relay-setting-row__dot--off" />
          <span className="relay-setting-row__label">차단 유예값</span>
          <input
            className="relay-setting-row__input"
            type="number"
            step="any"
            value={toInputValue(values[offKey])}
            disabled={disabled}
            onChange={(e) => onChange(offKey, e.target.value)}
            aria-label={`${def.title} 차단 유예값`}
          />
          <span className="relay-setting-row__text">{unitText}</span>
        </div>
      </div>
    </section>
  );
}

function RelayControlSetting({ open, onClose, onSaved }) {
  const [values, setValues] = useState(DEFAULT_RELAY_SETTINGS);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;

    const loadSettings = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(`${config.backendUrl}/api/relay-control-settings`);
        if (!res.ok) {
          throw new Error(`설정 조회 실패 (${res.status})`);
        }
        const data = await res.json();
        if (!cancelled) {
          setValues({ ...DEFAULT_RELAY_SETTINGS, ...data });
        }
      } catch (e) {
        if (!cancelled) {
          setError(e.message || "설정을 불러오지 못했습니다.");
          setValues(DEFAULT_RELAY_SETTINGS);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadSettings();
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape" && !saving) onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, saving, onClose]);

  if (!open) return null;

  const handleChange = (key, raw) => {
    setValues((prev) => ({ ...prev, [key]: raw }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");

    const payload = {};
    for (const key of Object.keys(DEFAULT_RELAY_SETTINGS)) {
      const num = Number(values[key]);
      if (values[key] === "" || Number.isNaN(num)) {
        setError("모든 차단 기준 값·유예값을 숫자로 입력해 주세요.");
        setSaving(false);
        return;
      }
      payload[key] = num;
    }

    try {
      const res = await fetch(`${config.backendUrl}/api/relay-control-settings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || data.message || `저장 실패 (${res.status})`);
      }
      onSaved?.(payload);
      onClose?.();
    } catch (e) {
      setError(e.message || "저장 중 오류가 발생했습니다.");
    } finally {
      setSaving(false);
    }
  };

  const busy = loading || saving;

  return (
    <div
      className="relay-setting-overlay"
      onClick={() => {
        if (!saving) onClose?.();
      }}
    >
      <div
        className="relay-setting-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="relay-setting-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="relay-setting-title" className="relay-setting-modal__title">
          접점 제어 차단 기준 설정
        </h2>
        <p className="relay-setting-modal__desc">
          채널별 차단 기준 값과 차단 유예값을 설정합니다. 접점 on일 시, 태양광
          판넬이 세워져 작물에게 날씨 영향을 더 줄 수 있습니다.
        </p>

        <div className="relay-setting-modal__info">
          <i className="relay-setting-modal__info-icon" aria-hidden="true">
            i
          </i>
          <span>
            <strong>차단 기준값</strong>은 접점 제어를 시작하는 기준입니다.
            <strong> 차단 유예값</strong>은 기준값 기준 ± 허용 구간으로,
            이 구간 안에서는 제어하지 않고, 유예 구간을 벗어난 값부터 제어합니다.
          </span>
        </div>

        <div className="relay-setting-modal__channels">
          {CHANNEL_DEFS.map((def) => (
            <ChannelCard
              key={def.key}
              def={def}
              values={values}
              onChange={handleChange}
              disabled={busy}
            />
          ))}
        </div>

        {error ? <p className="relay-setting-modal__error">{error}</p> : null}

        <div className="relay-setting-modal__footer">
          <button
            type="button"
            className="relay-setting-modal__btn relay-setting-modal__btn--cancel"
            onClick={onClose}
            disabled={saving}
          >
            취소
          </button>
          <button
            type="button"
            className="relay-setting-modal__btn relay-setting-modal__btn--save"
            onClick={handleSave}
            disabled={busy}
          >
            {saving ? "저장 중..." : "설정 저장"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default RelayControlSetting;
